<template>
  <div class="mrput-home-page">
    <!-- 顶部跑马灯促销文案；关闭后 sessionStorage 记录，本次会话不再显示 -->
    <div
      v-show="state.tickerVisible"
      class="ticker"
      id="ticker"
    >
      <div class="ticker-inner">
        <span
          v-for="(line, idx) in tickerLines"
          :key="'t1-' + idx"
        >
          {{ line }}
        </span>
        <span
          v-for="(line, idx) in tickerLines"
          :key="'t2-' + idx"
        >
          {{ line }}
        </span>
      </div>
      <button
        class="ticker-close"
        @click="closeTicker"
        aria-label="Close"
      >
        ×
      </button>
    </div>

    <!-- 顶栏：品牌、外链导航、主题切换、移动端汉堡按钮 -->
    <nav class="navbar">
      <router-link to="/home">
        <img
          class="nav-brand"
          fetchpriority="high"
          :src="homeInfo?.homeContent?.pcImage1"
          alt=""
        >
      </router-link>
      <ul class="nav-links">
        <li
          v-for="item in navList"
          :key="item.href"
        >
          <a
            v-if="item.href.startsWith('http')"
            :href="item.href"
            target="_blank"
          >
            {{ item.label }}
          </a>
          <router-link
            v-else
            :to="item.href"
          >
            {{ item.label }}
          </router-link>
        </li>
      </ul>
      <div class="nav-tools">
        <!-- 代购平台选择 -->
        <div class="platform-wrap">
          <SelectPurchasingPlatform
		  v-show="(!state.mobileMenuOpen&&homeInfo.showPlatform=='Y') || (state.mobileMenuOpen&&homeInfo.mobileShowPlatform=='Y')"
            :expert="expertConfig.expert"
            @handleChangeExpert="handleChangeExpert"
          />
        </div>

        <button
          class="nav-hamburger"
          @click="toggleMobile"
          aria-label="Menu"
          style="outline: none;"
        >
          ☰
        </button>
      </div>
    </nav>

    <!-- 移动端全屏抽屉菜单；:class.open 控制显隐，链接触发后收起 -->
    <div
      class="mobile-menu"
      id="mobileMenu"
      :class="{ open: state.mobileMenuOpen }"
    >
      <button
        class="mobile-close"
        @click="toggleMobile"
        aria-label="Close"
      >
        ×
      </button>

      <router-link
        class="nav-link"
        v-for="item in navList.slice(0, 2)"
        :key="item.href"
        :to="item.href"
        @click="toggleMobile"
      >
        {{ item.label }}
      </router-link>

      <a
        v-if="navList.length > 2"
        class="nav-link"
        :href="navList[2].href"
        target="_blank"
        @click="toggleMobile"
      >
        {{ navList[2].label }}
      </a>

      <div class="menu-divider"></div>

      <SelectPurchasingPlatform
		v-show="(!state.mobileMenuOpen&&homeInfo.showPlatform=='Y') || (state.mobileMenuOpen&&homeInfo.mobileShowPlatform=='Y')"
        :expert="expertConfig.expert"
        @handleChangeExpert="handleChangeExpert"
      />
    </div>

    <!-- 首屏 Hero：主标题、CTA、跳转店铺搜索表单（GET 到 /shop?q=） -->
    <section class="hero">
      <h1 class="hero-title">
        <span
          class="gradient-text"
          v-html="heroTitleLine"
        ></span>
      </h1>
      <p class="hero-sub">
        {{ heroSub }}
      </p>
      <div class="hero-actions">
        <router-link
          to="/goods"
          class="btn btn-primary"
          style="color: #ffffff !important;"
        >
          Shop Now
        </router-link>
      </div>

      <el-input
        v-model="state.searchValue"
        prefix-icon="el-icon-search"
        placeholder="Search"
        autocomplete="off"
        @keyup.enter.native="goGoodsSearch"
      ></el-input>
    </section>

    <!-- tab：锚点跳转当前页 #cat-*，activeTab 与 hash 同步；接口失败或无数据不展示 -->
    <nav
      v-show="state.catTabs.length"
      class="cat-strip"
    >
      <a
        v-for="tab in state.catTabs"
        :key="tab.id"
        class="cat-strip-item"
        :class="{ active: state.activeTab === tab.id }"
        :href="'#cat-' + tab.id"
        @click="selectCatTab(tab)"
      >
        {{ tab.label }}
      </a>
    </nav>

    <!-- 新品 -->
    <section
      id="cat-all"
      class="conveyor-section reveal visible"
    >
      <div class="conveyor-header">
        <h2 class="conveyor-title">
          Newest Finds
        </h2>
        <p class="conveyor-sub">
          Fresh drops added daily
        </p>
      </div>

      <div
        ref="conveyorWrapRef"
        class="conveyor-wrap"
        :class="{ 'is-static': state.conveyorStatic }"
      >
        <div class="conveyor-fade-left"></div>
        <div class="conveyor-fade-right"></div>
        <!-- 需滚动时 conveyorDisplayRows 为双份序列，供 initConveyor 无缝衔接；否则单份并居中 -->
        <div
          ref="conveyorTrackRef"
          class="conveyor-track"
        >
          <div
            v-for="row in conveyorDisplayRows"
            :key="row._key"
            class="product-card"
          >
            <div class="product-img-wrap">
              <div class="product-badge">
                New
              </div>
              <img
                class="product-img"
                :src="row.goodsImage"
                :alt="row.goodsName"
                loading="lazy"
                @error="onImgError"
              >
            </div>
            <div class="product-info">
              <div class="product-name">
                {{ row.goodsName }}
              </div>
              <div class="product-pricing">
                <span class="product-price">
                  {{ row.price }} {{ row.currencySymbol || '$' }}
                </span>
              </div>
              <span
                class="product-buy"
                @click.stop="onConveyorBuyClick(row)"
              >
                Buy on {{ selectPlatform?.platformName }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 分类区块 -->
    <classify
      v-for="tab in state.catTabs"
      :key="tab.id"
      :categoryName="tab.label"
      :categoryId="tab.id"
      :selectPlatform="selectPlatform"
    />

    <!-- 暂无独立类目区块时仍提供锚点，供横条滚动定位 -->
    <div
      id="cat-watches"
      class="cat-anchor"
    ></div>

    <div
      id="cat-tech"
      class="cat-anchor"
    ></div>

    <!-- 页脚：品牌说明、Shop/Tools/Legal 外链列、版权与 Powered by -->
    <footer class="footer">
      <div class="footer-inner">
        <div>
          <div class="footer-brand-text">
            {{ footer.brand }}
          </div>
          <p class="footer-desc">
            {{ footer.desc }}
          </p>
        </div>
      </div>
      <!-- <div class="footer-bottom">
        <span class="footer-copy">
          {{ footer.copy }}
        </span>
        <span class="footer-copy">
          {{ footer.poweredBy }}
        </span>
      </div> -->
    </footer>

    <!-- Cookie 提示条：未写入 cookies_accepted 时显示；接受/拒绝均写入 localStorage 并关闭 -->
    <!-- <div
      class="cookie-banner"
      id="cookieBanner"
      :class="{ active: state.cookieBannerActive }"
    >
      <p>
        We use cookies to improve your experience. By continuing, you agree to our
        <a href="">
          Privacy Policy
        </a>.
      </p>
      <div class="cookie-btns">
        <button
          class="btn btn-primary"
          @click="acceptCookies"
          style="padding:8px 20px;font-size:12px;"
        >
          Accept
        </button>
        <button
          class="btn btn-ghost"
          @click="declineCookies"
          style="padding:8px 20px;font-size:12px;"
        >
          Decline
        </button>
      </div>
    </div> -->
  </div>
</template>

<script>
import SelectPurchasingPlatform from '@/components/selectPurchasingPlatform.vue';
import classify from './components/classify.vue'
import productApi from '@/api/product'

/**
 * 分类项生成锚点 id：优先后端 id，避免与 hash 规则冲突
 * @param {object} item
 * @param {number} idx
 */
function catTabId(item, idx) {
  const raw = item.categoryId != null ? item.categoryId : item.id
  if (raw != null && raw !== '') return String(raw)
  return 'c' + idx
}

/** 主题与本地/会话存储键名（避免魔法字符串散落） */
const STORAGE_COOKIES = 'cookies_accepted'
const SESSION_TICKER = 'ticker_closed'
const SESSION_POPUP = 'popup_shown'
/**
 * 无限传送带：将轨道内容复制一份后，通过 translateX 循环；平移到 scrollWidth/2 时归零。
 * 悬停/触摸时 targetSpeed 降为 0，松手后恢复，避免误触时仍在快速滑动。
 * @returns 卸载函数：取消 rAF、移除监听，防止内存泄漏
 */
function initConveyor(trackEl) {
  if (!trackEl) return
  let pos = 0
  let speed = 0.5
  let targetSpeed = 0.5
  // 轨道内 DOM 为「两段相同商品」拼接，取一半宽度作为循环周期
  let halfWidth = trackEl.scrollWidth / 2
  if (halfWidth <= 0) return
  // 拖拽状态：支持鼠标与触摸横向跟手滑动
  const drag = {
    active: false,
    axis: '',
    startX: 0,
    startY: 0,
    lastX: 0
  }

  const clampPos = (v) => {
    if (halfWidth <= 0) return 0
    let next = v
    while (next < 0) next += halfWidth
    while (next >= halfWidth) next -= halfWidth
    return next
  }
  const pointOf = (ev) => {
    if (ev.touches && ev.touches.length) return ev.touches[0]
    if (ev.changedTouches && ev.changedTouches.length) return ev.changedTouches[0]
    return ev
  }

  const onEnter = () => {
    if (drag.active) return
    targetSpeed = 0
  }
  const onLeave = () => {
    if (drag.active) return
    targetSpeed = 0.5
  }
  const onDragStart = (ev) => {
    const p = pointOf(ev)
    drag.active = true
    drag.axis = ''
    drag.startX = p.clientX
    drag.startY = p.clientY
    drag.lastX = p.clientX
    speed = 0
    targetSpeed = 0
  }
  const onDragMove = (ev) => {
    if (!drag.active) return
    const p = pointOf(ev)
    const moveX = p.clientX - drag.startX
    const moveY = p.clientY - drag.startY
    if (!drag.axis) {
      if (Math.abs(moveX) < 6 && Math.abs(moveY) < 6) return
      drag.axis = Math.abs(moveX) >= Math.abs(moveY) ? 'x' : 'y'
    }
    if (drag.axis !== 'x') return
    if (ev.cancelable) ev.preventDefault()
    const stepX = p.clientX - drag.lastX
    drag.lastX = p.clientX
    pos = clampPos(pos - stepX)
    trackEl.style.transform = 'translateX(' + -pos + 'px)'
  }
  const onDragEnd = () => {
    if (!drag.active) return
    drag.active = false
    drag.axis = ''
    setTimeout(() => {
      targetSpeed = 0.5
    }, 2000)
  }

  trackEl.addEventListener('mouseenter', onEnter)
  trackEl.addEventListener('mouseleave', onLeave)
  trackEl.addEventListener('mousedown', onDragStart)
  trackEl.addEventListener('mousemove', onDragMove)
  trackEl.addEventListener('mouseleave', onDragEnd)
  trackEl.addEventListener('mouseup', onDragEnd)
  trackEl.addEventListener('touchstart', onDragStart, { passive: true })
  trackEl.addEventListener('touchmove', onDragMove, { passive: false })
  trackEl.addEventListener('touchend', onDragEnd)
  trackEl.addEventListener('touchcancel', onDragEnd)

  let running = true
  let conveyorRaf = 0
  function step() {
    if (!running) return
    // 当前速度向目标速度逼近，悬停时缓慢停下而非瞬间静止
    speed += (targetSpeed - speed) * 0.02
    pos += speed
    if (pos >= halfWidth) pos -= halfWidth // 超过半宽则减去周期，视觉上无缝
    trackEl.style.transform = 'translateX(' + -pos + 'px)'
    conveyorRaf = requestAnimationFrame(step)
  }
  conveyorRaf = requestAnimationFrame(step)

  return () => {
    running = false
    cancelAnimationFrame(conveyorRaf)
    trackEl.removeEventListener('mouseenter', onEnter)
    trackEl.removeEventListener('mouseleave', onLeave)
    trackEl.removeEventListener('mousedown', onDragStart)
    trackEl.removeEventListener('mousemove', onDragMove)
    trackEl.removeEventListener('mouseleave', onDragEnd)
    trackEl.removeEventListener('mouseup', onDragEnd)
    trackEl.removeEventListener('touchstart', onDragStart)
    trackEl.removeEventListener('touchmove', onDragMove)
    trackEl.removeEventListener('touchend', onDragEnd)
    trackEl.removeEventListener('touchcancel', onDragEnd)
  }
}

/**
 * 从 location.hash 解析类目 tab id（#cat-shoes -> shoes）
 * @param {string} hash
 * @returns {string}
 */
function tabIdFromHash(hash) {
  if (!hash || hash === '#') return 'all'
  const m = /^#cat-([\w-]+)$/.exec(hash)
  return m ? m[1] : 'all'
}

export default {
  name: 'HomeType2',

  components: {
    SelectPurchasingPlatform,
    classify,
  },

  props: {
    homeInfo: {
      type: Object,
      default: () => ({})
    },

    expertConfig: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      selectPlatform: {},
      state: {
        /** 类目横条当前选中 tab（与 catTabs[].id 对应） */
        activeTab: '',
        /** 类目 tabs：拉取成功前有数据再展示；失败则为空不展示 */
        catTabs: [],
        /**
         * 传送带：单份内容总宽 ≤ 容器时不滚动（卡片居中）；超出则双份 DOM 并 initConveyor
         */
        conveyorStatic: true,
        tickerVisible: true,
        mobileMenuOpen: false,
        cookieBannerActive: false,
        signupPopupActive: false,
        salesNotifVisible: true,
        salesNotifHiding: false,
        salesNotifName: 'Adidas Samba',
        salesNotifTime: '12 min ago',
        searchValue: '',
      }
    }
  },

  computed: {
    tickerLines() {
      return this.homeInfo?.homeContent?.title1?.split('##') || [];
    },

    heroTitleLine() {
      const arr = this.homeInfo?.homeContent?.title2?.split('##') || []

      if (arr.length > 1) {
        return arr.slice(0, arr.length - 1).join('<br>')
      }

      return arr[0] || ''
    },

    heroSub() {
      const arr = this.homeInfo?.homeContent?.title2?.split('##') || []

      if (arr.length > 1) {
        return arr.pop() || ''
      }

      return ''
    },

    footer() {
      const arr = this.homeInfo?.homeContent?.title3?.split('##') || []

      if (arr.length > 0) {
        return {
          brand: arr[0] || '',
          desc: arr[1] || ''
        }
      }

      return {
        brand: '',
        desc: '',
        copy: '© 2026 MrPutYouOnFinds. All rights reserved.',
        poweredBy: 'Powered by KakoBuy'
      }
    },

    navList() {
      const arr = [
        { href: '/home', label: 'HOME' },
        { href: '/goods', label: 'PRODUCTS' }
      ]

      if (this.homeInfo?.excelUrl) {
        arr.push({ href: this.homeInfo?.excelUrl, label: 'SPREADSHEET' })
      }

      return arr
    },

    /** 「Newest Finds」列表（接口 goodsList） */
    newestRows() {
      const list = this.homeInfo?.goodsList?.['Newest Finds']
      return Array.isArray(list) ? list : []
    },

    /**
     * 传送带渲染行：静态模式单份；滚动模式双份相同序列（与 initConveyor 半宽循环一致）
     */
    conveyorDisplayRows() {
      const rows = this.newestRows
      if (!rows.length) return []
      if (this.state.conveyorStatic) {
        return rows.map((r) => ({ ...r, _key: 's-' + r.id }))
      }
      return [
        ...rows.map((r) => ({ ...r, _key: 'a-' + r.id })),
        ...rows.map((r) => ({ ...r, _key: 'b-' + r.id }))
      ]
    }
  },

  watch: {
    /** 接口异步补数据后重新测量轨道 */
    newestRows: {
      handler() {
        this.$nextTick(() => this.scheduleConveyorResize())
      },
      deep: true
    },
    /** 达人 id 就绪后拉取分类，写入 catTabs */
    'expertConfig.expert.id': {
      handler(id) {
        if (id) this.fetchCatTabs()
      },
      immediate: true
    }
  },

  mounted() {
    if (sessionStorage.getItem(SESSION_TICKER)) {
      this.state.tickerVisible = false
    }

    const cookies = localStorage.getItem(STORAGE_COOKIES)
    if (!cookies) {
      this.state.cookieBannerActive = true
    }

    if (!sessionStorage.getItem(SESSION_POPUP)) {
      this.state.signupPopupActive = true
    }

    this.$nextTick(() => {
      const reveals = document.querySelectorAll('.reveal')
      this._revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('visible')
              this._revealObserver.unobserve(entry.target)
            }
          })
        },
        { threshold: 0.1 }
      )
      reveals.forEach((el) => this._revealObserver.observe(el))

      this.updateConveyorMode()

      const wrapEl = this.$refs.conveyorWrapRef
      if (wrapEl && typeof ResizeObserver !== 'undefined') {
        // 推迟到下一帧再测宽，避免与 RO 同帧布局形成「undelivered notifications」告警
        this._conveyorResizeObs = new ResizeObserver(() => {
          this.scheduleConveyorResize()
        })
        this._conveyorResizeObs.observe(wrapEl)
      } else {
        this._onConveyorWinResize = () => this.scheduleConveyorResize()
        window.addEventListener('resize', this._onConveyorWinResize)
      }
    })

    // fetch('/api/track-pageview', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ page: window.location.pathname })
    // }).catch(() => {})

    this._onCatHashChange = () => {
      this.syncCatTabFromHash()
    }
    window.addEventListener('hashchange', this._onCatHashChange)
    this.syncCatTabFromHash()
  },

  beforeDestroy() {
    if (this._revealObserver) this._revealObserver.disconnect()
    if (this._conveyorResizeObs) {
      this._conveyorResizeObs.disconnect()
      this._conveyorResizeObs = null
    }
    if (this._onConveyorWinResize) {
      window.removeEventListener('resize', this._onConveyorWinResize)
      this._onConveyorWinResize = null
    }
    if (this._conveyorResizeRaf) {
      cancelAnimationFrame(this._conveyorResizeRaf)
      this._conveyorResizeRaf = 0
    }
    if (this._stopConveyor) this._stopConveyor()
    if (this._salesTimer) clearTimeout(this._salesTimer)
    if (this._salesInterval) clearInterval(this._salesInterval)
    if (this._onCatHashChange) {
      window.removeEventListener('hashchange', this._onCatHashChange)
    }
    document.body.style.overflow = ''
  },

  methods: {
    /**
     * 初始化类目横条：getGoodsCategoryList(expertId) -> catTabs（首项固定 All）
     */
    async fetchCatTabs() {
      const expertId = this.expertConfig?.expert?.id
      if (!expertId) return
      try {
        const res = this.homeInfo?.goodsList?.['Categories']
        const raw = Array.isArray(res) ? res : []
        const tabs = []
        raw.forEach((item, idx) => {
          const label = item.categoryName
          if (!label) return
          tabs.push({ id: catTabId(item, idx), label })
        })
        this.state.catTabs = tabs
        this.$nextTick(() => this.syncCatTabFromHash())
      } catch (e) {
        console.warn('[HomeType2] fetchCatTabs failed', e)
        this.state.catTabs = []
      }
    },

    /**
     * 合并多次尺寸回调，并在下一帧再测量，避免 ResizeObserver 与布局同帧嵌套触发报错
     */
    scheduleConveyorResize() {
      if (this._conveyorResizeRaf) return
      this._conveyorResizeRaf = requestAnimationFrame(() => {
        this._conveyorResizeRaf = 0
        this.updateConveyorMode()
      })
    },

    /**
     * 根据单份内容宽度与容器比较：不足则静态居中不滚动；足够则双份并启动无限滚动
     */
    updateConveyorMode() {
      const wrapEl = this.$refs.conveyorWrapRef
      const trackEl = this.$refs.conveyorTrackRef
      if (!wrapEl || !trackEl) return

      if (this._stopConveyor) {
        this._stopConveyor()
        this._stopConveyor = null
      }
      trackEl.style.transform = ''

      const rows = this.newestRows
      if (!rows.length) {
        this.state.conveyorStatic = true
        return
      }

      const wrapW = wrapEl.clientWidth
      const trackW = trackEl.scrollWidth
      const singleW = this.state.conveyorStatic ? trackW : trackW / 2
      const needsScroll = singleW > wrapW + 1

      if (needsScroll && this.state.conveyorStatic) {
        this.state.conveyorStatic = false
        this.$nextTick(() => this.scheduleConveyorResize())
        return
      }
      if (!needsScroll && !this.state.conveyorStatic) {
        this.state.conveyorStatic = true
        this.$nextTick(() => this.scheduleConveyorResize())
        return
      }

      if (needsScroll) {
        this._stopConveyor = initConveyor(trackEl)
      }
    },

    handleChangeExpert(platform) {
      this.selectPlatform = platform;
    },

    /** 传送带购买按钮（仅首段 track 埋点） */
    onConveyorBuyClick(goods) {
      if (!this.selectPlatform.platformId) {
        return this.$message.warning(this.$t('selectPlatform'));
      }
      var url = this.selectPlatform.platformUrl;
      if (url.indexOf('{goodId}') != -1) {
        url = url.replace('{goodId}', goods.weidianGoodsId);
      }
      if (url.indexOf('{weidian}') != -1) {
        url = url.replace('{weidian}', goods.weidianId);
      }
      if (url.indexOf('{inviteCode}') != -1) {
        url = url.replace('{inviteCode}', this.selectPlatform.invitationCode);
      }
      window.open(url);
    },

    /**
     * 回车：带关键词跳转商品页（query.keyword 与商品页搜索框同步）
     */
    goGoodsSearch() {
      const raw = this.state.searchValue
      const keyword = (typeof raw === 'string' ? raw : String(raw ?? '')).trim()
      if (keyword.length <= 0) return
      this.state.mobileMenuOpen = false
      document.body.style.overflow = ''
      this.$router.push({
        path: '/goods',
        query: { keyword }
      })
    },

    /** 根据 hash 同步 activeTab（含前进后退） */
    syncCatTabFromHash() {
      const id = tabIdFromHash(window.location.hash)
      const valid = this.state.catTabs.some((t) => t.id === id)
      this.state.activeTab = valid ? id : 'all'
    },

    /** 类目横条：点击 a 跳转 #cat-* 并更新选中态 */
    selectCatTab(tab) {
      if (!tab || !tab.id) return
      this.state.activeTab = tab.id
    },

    /** 用户关闭顶部 ticker，会话内不再展示 */
    closeTicker() {
      this.state.tickerVisible = false
      sessionStorage.setItem(SESSION_TICKER, '1')
    },

    /** 切换移动端全屏菜单，并控制 body 滚动穿透 */
    toggleMobile() {
      this.state.mobileMenuOpen = !this.state.mobileMenuOpen
      document.body.style.overflow = this.state.mobileMenuOpen ? 'hidden' : ''
    },

    /** 接受 Cookie：写入 localStorage，关闭横幅（后续进入站点不再询问） */
    acceptCookies() {
      localStorage.setItem(STORAGE_COOKIES, '1')
      this.state.cookieBannerActive = false
    },

    /** 拒绝 Cookie：同样写入标记，仅关闭横幅（业务上可按需区分 '0'） */
    declineCookies() {
      localStorage.setItem(STORAGE_COOKIES, '0')
      this.state.cookieBannerActive = false
    },

    /** 商品图加载失败时用表面色块兜底，避免裂图 */
    onImgError(e) {
      const el = e.target
      if (el && el.style) el.style.background = 'var(--surface)'
    },
  }
}
</script>

<style lang="scss" scoped>
@import './styles/mr-put-you-on-finds-fonts.css';

@import './styles/type2-style.scss';

.nav-link:hover {
  color: var(--primary-color) !important;
}

.nav-tools {
  display: flex;
  gap: 8px;
  align-items: center;
}

.platform-wrap {
  display: flex;
  gap: 16px;
}

:deep(.el-input__inner) {
  color: var(--select-color) !important;
  border: 1px solid hsl(from var(--select-border-color) h s l / 0.8) !important;
  background-color: hsl(from var(--select-border-color) h s l / 0.5);
}

@media (max-width: 768px) {
  .platform-wrap {
    display: none;
  }
}
</style>
